import React, { useEffect, useMemo, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { FormContext } from "@features/port-security/components/SecurityClearence.types.ts";
import { buildCountryOptions } from "@features/port-security/components/utils";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { stringToTitleCase } from "@utils/formatters/strings";
import { redirectForTranslateFromEnglishToArabic } from "@utils/translateFromEnglishToArabic";
import { t } from "i18next";
import { FormProvider, useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

// Define the structure of the nationality object returned from the BE response
type Nationality = {
  nationality: string;
  nationalityArabic: string;
  type: string;
  active: boolean;
  visaRequired: boolean;
  securityFormReq: boolean;
};

const UAE_NATIONALITY = "United Arab Emirates";

const UAE_FIELDS: Array<keyof PortSecurityFormData> = [
  "emiratesIdNumber",
  "emiratesIdPlaceOfIssue",
  "emiratesIdDateOfIssue",
  "emiratesIdFront",
  "emiratesIdBack",
];

const NON_UAE_FIELDS: Array<keyof PortSecurityFormData> = [
  "passportNumber",
  "passportPlaceOfIssue",
  "passportDateOfIssue",
  "passportExpiryDate",
  "passportFront",
  "passportBack",
  "visaPlaceOfIssue",
  "visaType",
  "visaExpiryDate",
  "visa",
];

function capitalizeWords(str: string): string {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
}
const BioData: React.FC = () => {
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );
  const { options: countries } = useFormattedReference<{
    countryName: string;
    countryCode: string;
  }>(REFERENCE_DATA_TYPES.COUNTRIES, "bff-gatepass");

  const sortedNationalities = useMemo(
    () =>
      [...nationalities]
        .filter((n) => !!(n?.nationality?.trim() && n?.nationalityArabic?.trim()))
        .sort((a, b) =>
          a.nationality.localeCompare(b.nationality, undefined, { sensitivity: "base" }),
        ),
    [nationalities],
  );

  // Keep both dropdowns aligned by using the same English-sorted source list.
  const nationalitiesOptions = useMemo(
    () =>
      sortedNationalities.map((nationality) => ({
        value: nationality.nationality,
        label: nationality.nationality,
      })),
    [sortedNationalities],
  );
  const nationalitiesArabicOptions = useMemo(
    () =>
      sortedNationalities.map((nationality) => ({
        value: nationality.nationalityArabic,
        label: nationality.nationalityArabic,
      })),
    [sortedNationalities],
  );
  const countriesOptions = useMemo(
    () => buildCountryOptions(countries, { valueMode: "code", formatName: stringToTitleCase }),
    [countries],
  );

  const enToAr = useMemo(
    () =>
      new Map(
        sortedNationalities.map((nationality) => [
          nationality.nationality,
          nationality.nationalityArabic,
        ]),
      ),
    [sortedNationalities],
  );
  const arToEn = useMemo(
    () =>
      new Map(
        sortedNationalities.map((nationality) => [
          nationality.nationalityArabic,
          nationality.nationality,
        ]),
      ),
    [sortedNationalities],
  );

  const presentNationalityEnglish = useWatch({
    control: formData.control,
    name: "presentNationality",
  });
  const presentNationalityArabic = useWatch({
    control: formData.control,
    name: "presentNationalityArabic",
  });

  const syncingRef = useRef(false);
  const previousNationalityRef = useRef<string | undefined>(presentNationalityEnglish);

  useEffect(() => {
    if (syncingRef.current || !sortedNationalities.length) return;

    const syncValues = (
      source: string | undefined,
      target: string | undefined,
      map: Map<string, string>,
      targetField: string,
    ) => {
      if (source && (!target || target !== map.get(source))) {
        syncingRef.current = true;
        updateFormData(targetField, map.get(source) || "");
        queueMicrotask(() => (syncingRef.current = false));
      }
    };

    syncValues(
      presentNationalityEnglish,
      presentNationalityArabic,
      enToAr,
      "presentNationalityArabic",
    );
    syncValues(presentNationalityArabic, presentNationalityEnglish, arToEn, "presentNationality");
  }, [
    sortedNationalities,
    presentNationalityEnglish,
    presentNationalityArabic,
    enToAr,
    arToEn,
    updateFormData,
  ]);

  useEffect(() => {
    const currentNationality = presentNationalityEnglish?.trim();
    const previousNationality = previousNationalityRef.current?.trim();

    if (previousNationality === currentNationality) {
      return;
    }

    previousNationalityRef.current = presentNationalityEnglish;

    if (previousNationality == null) {
      return;
    }

    const inactiveFields = currentNationality === UAE_NATIONALITY ? NON_UAE_FIELDS : UAE_FIELDS;
    const hasInactiveErrors = inactiveFields.some((field) =>
      Boolean(formData.formState.errors[field]),
    );

    if (formData.formState.submitCount > 0) {
      void formData.trigger?.();
    } else if (hasInactiveErrors) {
      void formData.trigger?.(inactiveFields);
    }
  }, [formData, presentNationalityEnglish]);

  const handleNationalitiesChange = (
    field: keyof PortSecurityFormData,
    value: string | undefined,
    map: Map<string, string>,
    targetField: keyof PortSecurityFormData,
  ) => {
    if (syncingRef.current) return;

    if (!value) {
      syncingRef.current = true;
      formData.setValue(targetField, "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      updateFormData(field, value || "");
      updateFormData(targetField, "");
      queueMicrotask(() => (syncingRef.current = false));
      formData.trigger?.(["presentNationality", "presentNationalityArabic"]);
      return;
    }

    const mappedValue = map.get(value) || "";
    if (mappedValue) {
      syncingRef.current = true;
      formData.setValue(targetField, mappedValue, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      updateFormData(field, value);
      updateFormData(targetField, mappedValue);
      queueMicrotask(() => (syncingRef.current = false));
    }
  };

  const user = useAuthStore.getState().user;
  const roles = user?.roles || [];
  const fullNameEn = user?.name || "";
  const isUaeUser = roles.includes("uae-pass-user");
  const userMobile = (user as { mobileNumber?: string } | undefined)?.mobileNumber;

  useEffect(() => {
    if (fullNameEn && isUaeUser) {
      formData.setValue("fullNameEn", capitalizeWords(fullNameEn), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      updateFormData("fullNameEn", capitalizeWords(fullNameEn));
    }
  }, [fullNameEn]);

  useEffect(() => {
    if (userMobile && isUaeUser) {
      formData.setValue("mobileNumber", userMobile, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      updateFormData("mobileNumber", userMobile);
    }
  }, [userMobile, isUaeUser, formData, updateFormData]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.bio-data")}>
        <FormItem<PortSecurityFormData, "fullNameEn">
          name="fullNameEn"
          type="text"
          maxChars={200}
          label={t("security-clearance.full-name-en")}
          testId="security-fullNameEn"
          onFieldChange={updateFormData}
          disabled={isUaeUser}
        />
        <FormItem<PortSecurityFormData, "fullNameAr">
          dir="rtl"
          name="fullNameAr"
          type="text"
          maxChars={200}
          label={t("security-clearance.full-name-ar")}
          testId="security-fullNameAr"
          onFieldChange={updateFormData}
          actionInLabel
          textForActionInLabel={t("security-clearance.translate")}
          onActionClicked={() =>
            redirectForTranslateFromEnglishToArabic(formData.getValues("fullNameEn"))
          }
        />
        <FormItem<PortSecurityFormData, "surname">
          required
          name="surname"
          type="text"
          maxChars={200}
          label={t("security-clearance.surname")}
          testId="security-surname"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "alias">
          name="alias"
          type="text"
          maxChars={200}
          label={t("security-clearance.alias")}
          testId="security-alias"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "emailAddress">
          required
          name="emailAddress"
          type="text"
          maxChars={50}
          label={t("security-clearance.individual-email-address")}
          testId="security-emailAddress"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "mobileNumber">
          required
          name="mobileNumber"
          type="text"
          maxChars={200}
          label={t("security-clearance.individual-mobile-number")}
          testId="security-mobileNumber"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "placeOfBirth">
          required
          name="placeOfBirth"
          type="autocomplete"
          options={countriesOptions}
          label={t("security-clearance.place-of-birth")}
          testId="security-placeOfBirth"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "dateOfBirth">
          required
          name="dateOfBirth"
          type="date-picker"
          label={t("security-clearance.date-of-birth")}
          testId="security-dateOfBirth"
          onFieldChange={updateFormData}
          timePicker={false}
          dateConstraint="past"
        />
        <FormItem<PortSecurityFormData, "presentNationality">
          required
          name="presentNationality"
          type="autocomplete"
          options={nationalitiesOptions}
          label={t("security-clearance.present-nationality")}
          testId="security-presentNationality"
          onFieldChange={(_: string, value: unknown) =>
            handleNationalitiesChange(
              "presentNationality",
              (value as string) || undefined,
              enToAr,
              "presentNationalityArabic",
            )
          }
        />
        <FormItem<PortSecurityFormData, "presentNationalityArabic">
          required
          name="presentNationalityArabic"
          type="autocomplete"
          options={nationalitiesArabicOptions}
          label={t("security-clearance.present-nationality-arabic")}
          testId="security-presentNationalityArabic"
          onFieldChange={(_: string, value: unknown) =>
            handleNationalitiesChange(
              "presentNationalityArabic",
              (value as string) || undefined,
              arToEn,
              "presentNationality",
            )
          }
        />
        <FormItem<PortSecurityFormData, "pastNationality">
          name="pastNationality"
          type="autocomplete"
          options={nationalitiesOptions}
          label={t("security-clearance.past-nationality")}
          testId="security-pastNationality"
          onFieldChange={updateFormData}
        />
        <div /> {/* div used for spacing */}
        <FormItem<PortSecurityFormData, "sect">
          name="sect"
          type="text"
          maxChars={200}
          label={t("security-clearance.sect")}
          testId="security-sect"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "religion">
          name="religion"
          type="text"
          maxChars={200}
          label={t("security-clearance.religion")}
          testId="security-religion"
          onFieldChange={updateFormData}
        />
        <div className="grid col-span-2 grid-cols-3 gap-4">
          <FormItem<PortSecurityFormData, "placeOfEntry">
            required
            name="placeOfEntry"
            type="text"
            maxChars={200}
            label={t("security-clearance.place-of-entry")}
            testId="security-placeOfEntry"
            onFieldChange={updateFormData}
          />
          <FormItem<PortSecurityFormData, "dateOfEntry">
            required
            name="dateOfEntry"
            type="date-picker"
            label={t("security-clearance.date-of-entry")}
            testId="security-dateOfEntry"
            onFieldChange={updateFormData}
            timePicker={false}
          />
          <FormItem<PortSecurityFormData, "sponsorUponEntry">
            required
            name="sponsorUponEntry"
            type="text"
            maxChars={200}
            label={t("security-clearance.sponsor-upon-entry")}
            testId="security-sponsorUponEntry"
            onFieldChange={updateFormData}
          />
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default BioData;
